import { useEffect, useMemo, useRef, type MouseEvent } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { useTranslation } from "react-i18next";

import Header from "./components/Header";
import Footer from "./components/Footer";
import HomeBody from "./components/HomeBody";
import MessageEdit from "./components/MessageEdit";
import Plans from "./components/Plans";
import Profile from "./components/Profile";
import Preferences from "./components/Preferences";
import Checkout from "./components/Checkout";
import Faqs from "./components/Faqs";
import CustomerTestimonials from "./components/CustomerTestimonials";
import About from "./components/About";
import Features from "./components/Features";
import ContactUs from "./components/ContactUs";
import RecipientEdit from "./components/RecipientEdit";
import AttachmentEdit from "./components/AttachmentEdit";
import AttachmentView from "./components/AttachmentView";
import ManagementPanel from "./components/ManagementPanel";
import Dashboard from "./components/Dashboard";
import UserGuide from "./components/UserGuide";

import { useAutoLogout } from "./utilities/useAutoLogout";
import AutoLogoutWarningBanner from "./components/AutoLogoutWarningBanner";

import {
  UIMessageProvider,
  useUIMessage,
} from "./components/ui/UIMessageProvider";
import "./styleSheets/uiMessages.css";

import ComponentPlayground from "./playground/ComponentPlayground";
import AboutFull from "./components/AboutFull";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Verified from "./components/Verified";
import ScrollToTop from "./components/ScrollToTop";
import LegacyMessageView from "./components/LegacyMessageView";
import InheritedMessageView from "./components/InheritedMessageView";
import DeliveredMessageView from "./components/DeliveredMessageView";
import RecipientView from "./components/RecipientView";
import { NavigationGuardProvider } from "./contexts/NavigationGuardContext";
import { LegalGateProvider } from "./contexts/LegalGateContext";
import AuthGate from "./components/AuthGate";
import LegalAcceptance from "./components/LegalAcceptance";
import {
  initializeAnalytics,
  isPublicAnalyticsPath,
  trackPageView,
} from "./utilities/analytics";

function AppContent() {
  const testMode = false;
  const { showWarning, stayLoggedIn, warningTimeLeft } = useAutoLogout();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { toast } = useUIMessage();
  const lastToastKeyRef = useRef<string | null>(null);
  const mainContentRef = useRef<HTMLElement | null>(null);

  const normalizedPath = useMemo(() => {
    const rawPath = location.pathname.trim().toLowerCase();
    if (rawPath === "/") return "/";
    return rawPath.replace(/\/+$/, "");
  }, [location.pathname]);

  const publicPageTitles = useMemo(() => {
    const appTitle = "Eterniq - More Than a Message";
    return {
      "/": appTitle,
      "/register": `${t("header.menu.signInRegister", {
        defaultValue: "Sign in / Register",
      })} | ${appTitle}`,
      "/userguide": `${t("pageBody.support.userGuide", {
        defaultValue: "User guide",
      })} | ${appTitle}`,
      "/about": `${t("footer.menu.aboutUs", { defaultValue: "About Us" })} | ${appTitle}`,
      "/aboutfull": `${t("footer.menu.aboutUs", { defaultValue: "About Us" })} | ${appTitle}`,
      "/features": `${t("pageBody.features.cardsTitle", {
        defaultValue: "Key features",
      })} | ${appTitle}`,
      "/contactus": `${t("header.menu.contact", { defaultValue: "Contact Us" })} | ${appTitle}`,
      "/verified": `${t("verified.title", { defaultValue: "Verification status" })} | ${appTitle}`,
      "/plans": `${t("header.menu.pricing", { defaultValue: "Pricing & Plans" })} | ${appTitle}`,
      "/checkout": `${t("checkout.checkout", { defaultValue: "Checkout" })} | ${appTitle}`,
      "/faqs": `${t("header.menu.faqs", { defaultValue: "FAQs" })} | ${appTitle}`,
      "/customertestimonials": `${t("pageBody.testimonial.testimonialsTitle", {
        defaultValue: "What our customers say",
      })} | ${appTitle}`,
      "/termsofservice": `${t("footer.menu.terms", { defaultValue: "Terms of Service" })} | ${appTitle}`,
      "/privacypolicy": `${t("footer.menu.privPolicy", { defaultValue: "Privacy Policy" })} | ${appTitle}`,
      "/legalacceptance": `${t("legal.gate.pageTitle", {
        defaultValue: "Terms & Privacy Policy Update",
      })} | ${appTitle}`,
      "/not-found": `Page not found | ${appTitle}`,
    } as Record<string, string>;
  }, [t]);
  const notFoundTitle = "Page not found | Eterniq - More Than a Message";

  // Signed-in pages. Kept separate from publicPageTitles so they never feed analytics.
  // Keyed by the first path segment, mirroring the <Routes> below: `base` is the title
  // for the bare path, `withId` for the `/:param` variant; a missing entry means that
  // shape has no route and falls through to the not-found title.
  const privatePageTitles = useMemo(() => {
    const appTitle = "Eterniq - More Than a Message";
    const title = (key: string, defaultValue: string) =>
      `${t(key, { defaultValue })} | ${appTitle}`;
    const both = (value: string) => ({ base: value, withId: value });

    return {
      pages: {
        dashboard: { base: title("header.menu.dashboard", "Dashboard") },
        profile: { base: title("header.menu.profile", "Profile") },
        preferences: { base: title("preferences.title", "Preferences") },
        messageedit: {
          base: title("message.newMessage", "New message"),
          withId: title("message.editMessage", "Edit message"),
        },
        legacymessageview: both(title("message.legacyMessage", "Legacy message")),
        inheritedmessageview: both(
          title("message.inheritedMessage", "Inherited message"),
        ),
        "delivered-messages": {
          withId: title("deliveredMessage.deliveredMessage", "Delivered message"),
        },
        recipientview: both(title("recipient.myRecipients", "My recipients")),
        recipientedit: {
          base: title("recipient.newRecipient", "New recipient"),
          withId: title("recipient.editRecipient", "Edit recipient"),
        },
        attachmentview: both(title("file.editViewFileTitle", "File")),
        attachmentedit: {
          base: title("file.newFile", "New file"),
          withId: title("file.editFile", "Edit file"),
        },
      } as Record<string, { base?: string; withId?: string }>,
      panelTabs: {
        legacyMessages: title("message.myLegacyMessages", "My legacy messages"),
        recipients: title("recipient.myRecipients", "My recipients"),
        files: title("file.myFiles", "My files"),
        inheritedMessages: title(
          "inheritedMessage.inheritedMessages",
          "Inherited messages",
        ),
        deliveredMessages: title(
          "deliveredMessage.deliveredMessages",
          "Delivered Messages",
        ),
      } as Record<string, string>,
    };
  }, [t]);

  const pageTitle = useMemo(() => {
    const publicPageTitle = publicPageTitles[normalizedPath];
    if (publicPageTitle) return publicPageTitle;

    const segments = normalizedPath.split("/").filter(Boolean);
    if (segments.length > 2) return notFoundTitle;
    const [segment = "", id] = segments;

    if (segment === "panel" && !id) {
      const tab = new URLSearchParams(location.search).get("selectedTab") ?? "";
      const { panelTabs } = privatePageTitles;
      return Object.prototype.hasOwnProperty.call(panelTabs, tab)
        ? panelTabs[tab]
        : panelTabs.legacyMessages;
    }

    const page = privatePageTitles.pages[segment];
    return (id ? page?.withId : page?.base) ?? notFoundTitle;
  }, [
    location.search,
    normalizedPath,
    notFoundTitle,
    privatePageTitles,
    publicPageTitles,
  ]);

  useEffect(() => {
    const t = (location.state as any)?.toast;
    if (!t?.message) return;

    const key = `${location.key}:${t.type ?? "info"}:${t.message}`;
    if (lastToastKeyRef.current === key) return;
    lastToastKeyRef.current = key;

    if (t.type === "success") toast.success(t.message);
    else if (t.type === "error") toast.error(t.message);
    else toast.info(t.message);

    // Clear state so back/forward doesn't replay it
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.key, location.pathname, location.state, navigate, toast]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    if (!isPublicAnalyticsPath(normalizedPath)) {
      return;
    }

    initializeAnalytics();
    trackPageView(
      normalizedPath,
      publicPageTitles[normalizedPath] ?? document.title,
    );
  }, [normalizedPath, publicPageTitles]);

  const handleSkipToMain = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    mainContentRef.current?.focus();
  };

  const isLegalAcceptanceRoute = normalizedPath === "/legalacceptance";

  return (
    <>
      {testMode ? (
        <ComponentPlayground />
      ) : (
        <>
          <a className="skip-to-content" href="#main-content" onClick={handleSkipToMain}>
            {t("skipToMainContent", { defaultValue: "Skip to main content" })}
          </a>
          {showWarning && (
            <AutoLogoutWarningBanner
              onStayLoggedIn={stayLoggedIn}
              onWarningTimeLeft={warningTimeLeft}
            />
          )}
          {!isLegalAcceptanceRoute && <Header />}
          <ScrollToTop />
          <main id="main-content" className="app-main" tabIndex={-1} ref={mainContentRef}>
            <Routes>
              <Route element={<AuthGate requireAuth={false} />}>
                <Route path="/" element={<HomeBody />} />
                <Route path="/Register" element={<HomeBody />} />
              </Route>
              <Route element={<AuthGate requireAuth={true} />}>
                <Route path="/Dashboard" element={<Dashboard />} />
                <Route
                  path="/LegacyMessageView"
                  element={<LegacyMessageView />}
                />
                <Route
                  path="/LegacyMessageView/:message"
                  element={<LegacyMessageView />}
                />

                <Route
                  path="/InheritedMessageView"
                  element={<InheritedMessageView />}
                />
                <Route
                  path="/InheritedMessageView/:message"
                  element={<InheritedMessageView />}
                />

                <Route
                  path="/delivered-messages/:id"
                  element={<DeliveredMessageView />}
                />

                <Route path="/MessageEdit" element={<MessageEdit />} />
                <Route path="/MessageEdit/:message" element={<MessageEdit />} />

                <Route path="/RecipientView" element={<RecipientView />} />
                <Route
                  path="/RecipientView/:recipient"
                  element={<RecipientView />}
                />

                <Route path="/RecipientEdit" element={<RecipientEdit />} />
                <Route
                  path="/RecipientEdit/:recipient"
                  element={<RecipientEdit />}
                />

                <Route path="/AttachmentView" element={<AttachmentView />} />
                <Route
                  path="/AttachmentView/:attachment"
                  element={<AttachmentView />}
                />

                <Route path="/AttachmentEdit" element={<AttachmentEdit />} />
                <Route
                  path="/AttachmentEdit/:attachment"
                  element={<AttachmentEdit />}
                />

                <Route path="/Profile" element={<Profile />} />
                <Route path="/Preferences" element={<Preferences />} />
                <Route path="/Panel" element={<ManagementPanel />} />
                <Route path="/LegalAcceptance" element={<LegalAcceptance />} />
              </Route>
              <Route path="/UserGuide" element={<UserGuide />} />
              <Route path="/About" element={<About />} />
              <Route path="/AboutFull" element={<AboutFull />} />
              <Route path="/Features" element={<Features />} />
              <Route path="/ContactUs" element={<ContactUs />} />
              <Route path="/verified" element={<Verified />} />
              <Route path="/Plans" element={<Plans />} />
              <Route path="/Checkout" element={<Checkout />} />
              <Route path="/FAQs" element={<Faqs />} />
              <Route
                path="/CustomerTestimonials"
                element={<CustomerTestimonials />}
              />
              <Route path="/TermsOfService" element={<TermsOfService />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route
                path="*"
                element={
                  <section aria-labelledby="page-not-found-title">
                    <h1 id="page-not-found-title">Page not found</h1>
                  </section>
                }
              />
            </Routes>
          </main>
          {!isLegalAcceptanceRoute && <Footer />}
        </>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <UIMessageProvider>
        <NavigationGuardProvider>
          <LegalGateProvider>
            <AppContent />
          </LegalGateProvider>
        </NavigationGuardProvider>
      </UIMessageProvider>
    </Router>
  );
}

export default App;
